'use client';

import {useContext} from 'react';
import styled from '@emotion/styled';

import theme from 'theme';
import {startOfDay} from 'common';

import {CalendarContext} from './CalendarContext';
import {Block} from './Block';

import {CalendarMode} from './types';
import {useCalendar} from './hook';

import type {BaseCalendarProps, CalendarProps, RangeCalendarProps, RangeCalendarValue} from './types';
import type {RequiredQA} from 'common';
import type {ReactNode} from 'react';

const Container = styled.div`
  display: flex;
  align-items: flex-start;
  background: ${theme.colors.white};
  box-shadow: ${theme.shadows.medium};
  border-radius: 8px;
  box-sizing: border-box;
  width: fit-content;

  ${theme.typography.p2}
`;

const CalendarComponent: React.FC<RequiredQA & Pick<BaseCalendarProps, 'className' | 'components'>> = props => {
  const {qa, className, components} = props;
  const {blocks} = useContext(CalendarContext);

  const blockComponents: ReactNode[] = [];

  for (let blockNumber = 0; blockNumber < blocks; blockNumber++) {
    blockComponents.push(
      <Block key={blockNumber} qa={`${qa}-block-${blockNumber}`} blockNumber={blockNumber} components={components} />
    );
  }

  return (
    <Container data-qa={qa} className={className}>
      {blockComponents}
    </Container>
  );
};

export const BaseCalendar: React.FC<BaseCalendarProps> = ({qa = 'calendar', className, ...props}) => {
  const {context, components} = useCalendar(props);

  return (
    <CalendarContext.Provider value={context}>
      <CalendarComponent qa={qa} className={className} components={components} />
    </CalendarContext.Provider>
  );
};

export const RangeCalendar: React.FC<RangeCalendarProps> = props => {
  const [rangeStart, rangeEnd] = props.value ?? [];

  const value: RangeCalendarValue = [
    rangeStart ? startOfDay(rangeStart) : undefined,
    rangeEnd ? startOfDay(rangeEnd) : undefined,
  ];

  return <BaseCalendar mode={CalendarMode.Range} {...props} value={value} />;
};

export const Calendar: React.FC<CalendarProps> = ({value, onChange, ...props}) => {
  const override = {
    mode: CalendarMode.Single,

    value: [value ? startOfDay(value) : undefined, undefined],

    onChange: newValue => onChange(newValue[0]),
  } as BaseCalendarProps;

  return <BaseCalendar {...props} {...override} />;
};
